import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Calendar } from "lucide-react";

const navLinks = [
  { name: "Accueil", path: "/" },
  { name: "Simulateur", path: "/simulateur" },
  { name: "Services", path: "/services" },
  { name: "Feedbacks", path: "/feedbacks" },
  { name: "À propos", path: "/a-propos" },
  { name: "Blog", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <>
      <header
        className={`header-sticky transition-all duration-300 ${
          scrolled ? "py-3 shadow-sm" : "py-4"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src="/logo-freelanceurs.png" 
                alt="FREELANCEURS.io" 
                className="h-10 w-auto"
              />
              <span className="font-manrope font-bold text-xl text-slate-900 hidden sm:block">
                Freelanceurs<span className="text-[#52fbb0]">.io</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link font-inter text-sm ${
                    location.pathname === link.path ? "active text-slate-900" : ""
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Link
                to="/connect"
                className={`font-inter text-sm font-semibold px-4 py-2 rounded-full border-2 border-slate-900 transition-colors ${
                  location.pathname === "/connect" ? "bg-slate-900 text-white" : "text-slate-900 hover:bg-slate-900 hover:text-white"
                }`}
                data-testid="nav-connect-b2b"
              >
                Entreprise | BtoB
              </Link>
              <Link
                to="/rendez-vous"
                data-testid="cta-rdv-link"
              >
                <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-semibold rounded-full px-6 shadow-lg shadow-green-200/50 hover:shadow-green-300/50 hover:-translate-y-0.5 transition-all">
                  <Calendar className="mr-1 h-4 w-4" />
                  Prendre RDV
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="mobile-menu lg:hidden animate-fade-in" data-testid="mobile-menu">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-inter text-lg py-3 px-4 rounded-xl transition-colors ${
                  location.pathname === link.path
                    ? "bg-[#52fbb0]/10 text-slate-900 font-semibold"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/connect"
              className="font-inter text-lg py-3 px-4 rounded-xl border-2 border-slate-900 text-slate-900 font-semibold text-center"
              data-testid="mobile-nav-connect-b2b"
            >
              Entreprise | BtoB
            </Link>
            <Link
              to="/rendez-vous"
              className="mt-4"
            >
              <Button className="w-full bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-semibold rounded-full py-6 text-lg">
                <Calendar className="mr-2 h-5 w-5" />
                Prendre RDV
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </>
  );
};
